import { AllowedPartners, useExpertUniversityStore } from "@expert/expert-university";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { Partner } from "@expert/shared-types";
import { HTTPError } from "ky";

import { getSharedApiClient } from "@/shared-utils";

import type { CreateTrainingSessionRequest, TrainingSession, UpdateTrainingSessionRequest } from "../types";
import type {
    SessionEvaluationCompleted,
    SessionEvaluationError,
    SessionEvaluationResult,
    SessionEvaluationRetryExhausted,
    SessionEvaluationSkipped,
    StoredSessionReadResult,
    StoredSessionRecord,
    TranscriptionEntry,
} from "./evaluations/types";

import { getLiveKitSessionApiClient } from "./liveKitSessionApi";
import { getTrainingSessionApiClient } from "./trainingSessionApi";

const logger = getLogger({ module: "Expert University API" });

interface TrainingSessionApiResult {
    count: number;
    items: TrainingSession[];
}

interface CreateLiveKitSessionRequest {
    personaId: string;
    userId: string;
    platformId: string;
    trainingSessionId: string;
    partner: Partner;
    isAscendProfile: boolean;
}

interface EndLiveKitSessionRequest {
    sessionId: string;
}

interface CreateLiveKitSessionResponse {
    sessionId: string;
    lkUrl: string;
    userToken: string;
    roomName: string;
    userId: string;
}

// The training-session "update" route is a partial DynamoDB UpdateItem keyed by SessionID, shared by
// two callers: session-end updates (UpdateTrainingSessionRequest) and transcript flushes (this type).
// Only SessionID + Transcriptions are sent; other attributes on the item are left untouched.
interface UpdateTrainingSessionTranscriptionsRequest {
    SessionID: string;
    Transcriptions: TranscriptionEntry[];
}

const getErrorDetails = (error: unknown) => {
    if (error instanceof HTTPError) {
        return {
            error: error.message,
            statusCode: error.response.status,
            statusText: error.response.statusText,
        };
    }

    return {
        error: error instanceof Error ? error.message : String(error),
    };
};

const { sessionId: sessionIdFromStore } = useExpertUniversityStore.getState();

export const createLiveKitSession = async (
    request: CreateLiveKitSessionRequest,
): Promise<CreateLiveKitSessionResponse> => {
    try {
        logger.info({ ...request }, "Creating live kit session");
        const response = await getLiveKitSessionApiClient().post(`api/v1/training-assist/sessions`, {
            json: request,
        });
        return response.json();
    } catch (error: unknown) {
        logger.error({ ...getErrorDetails(error), personaId: request.personaId }, "Error creating live kit session");
        throw error;
    }
};

export const createTrainingSession = async (request: CreateTrainingSessionRequest) => {
    try {
        logger.info({ ...request, sessionId: request.SessionID }, "Creating training session");
        await getTrainingSessionApiClient()
            .extend({
                hooks: {
                    beforeRetry: [
                        ({ error, retryCount }) => {
                            logger.warn(
                                {
                                    ...getErrorDetails(error),
                                    retryCount,
                                    sessionId: request.SessionID,
                                    challengeId: request.ChallengeId,
                                },
                                "Retrying training session creation",
                            );
                        },
                    ],
                },
            })
            .post("create", {
                json: request,
            });
        trackFullStoryEvent("Training Session Created", { sessionId: request.SessionID });
    } catch (error: unknown) {
        logger.error(
            { ...getErrorDetails(error), sessionId: request.SessionID, challengeId: request.ChallengeId },
            "Error creating training session",
        );
        trackFullStoryEvent("Training Session Creation Failed", { sessionId: request.SessionID });
        throw error;
    }
};

export const endLiveKitSession = async (request: EndLiveKitSessionRequest) => {
    try {
        logger.info({ ...request, sessionId: request.sessionId }, "Ending live kit session");
        await getLiveKitSessionApiClient().delete(
            `api/v1/training-assist/sessions/${encodeURIComponent(request.sessionId)}`,
            {
                json: request,
            },
        );
    } catch (error: unknown) {
        logger.error({ ...getErrorDetails(error), sessionId: request.sessionId }, "Error ending live kit session");
    }
};

export const saveTranscriptions = async (sessionId: string, transcriptions: TranscriptionEntry[]): Promise<void> => {
    try {
        logger.info({ sessionId, count: transcriptions.length }, "Saving transcriptions");
        // Reuses the existing training-session "update" route, which does a partial UpdateItem keyed by
        // SessionID. Sending only Transcriptions overwrites that attribute (last-write-wins, so retries
        // can't duplicate) while leaving the evaluation lambda's EvaluationDetails on the same item intact.
        const payload: UpdateTrainingSessionTranscriptionsRequest = {
            SessionID: sessionId,
            Transcriptions: transcriptions,
        };
        await getTrainingSessionApiClient().post("update", { json: payload });
    } catch (error: unknown) {
        logger.warn({ ...getErrorDetails(error), sessionId }, "Error saving transcriptions");
        throw error;
    }
};

export const updateTrainingSession = async (request: UpdateTrainingSessionRequest) => {
    const sessionId = request.SessionID;
    try {
        logger.info({ ...request, sessionId }, "Updating training session");
        trackFullStoryEvent("Training Session Updated", { sessionId });
        await getTrainingSessionApiClient().post("update", {
            json: request,
        });
    } catch (error: any) {
        logger.info({ error: error.message, sessionId }, "Error updating training session");
        trackFullStoryEvent("Training Session Update Failed", { sessionId });
    }
};

export const getTrainingSessionsByEmployeeId = async (
    employeeId: string | null,
    partner: AllowedPartners,
    challengeId?: string,
    sessionId?: string,
): Promise<TrainingSessionApiResult | undefined> => {
    const taskId = sessionId ?? sessionIdFromStore;
    try {
        logger.info({ employeeId, taskId }, "Getting training sessions by employee ID");
        trackFullStoryEvent("Fetching Training Sessions", { employeeId, taskId });
        const response = await getTrainingSessionApiClient().post("query", {
            json: {
                EmployeeId: employeeId,
                ChallengeId: challengeId,
                SessionId: sessionId,
                Partner: partner,
            },
        });
        const trainingSessions = await response.json<TrainingSessionApiResult>();

        return trainingSessions;
    } catch (error: any) {
        logger.info({ error: error.message, taskId }, "Error getting training sessions by employee ID");
        trackFullStoryEvent("Fetching Training Sessions Failed", { employeeId, taskId });
    }
};

export const EVALUATION_POLL_REQUEST_TIMEOUT_MS = 5_000;

// Sized for the stored read's three attempts: warm, this single-item read answers in well under a
// second, so 6s is cold-start headroom that still caps the whole read near 19s (3 x 6s + 2 x 500ms)
// instead of minutes — and a cold start past 6s is better served by the next attempt.
export const EVALUATION_STORED_READ_TIMEOUT_MS = 6_000;

// The route's own miss. Any other 404 body (Fastify's route-not-found) means the route isn't deployed.
const SESSION_NOT_FOUND_MESSAGE = "Session not found";

const isSessionNotFound = (error: unknown) =>
    error instanceof HTTPError &&
    error.response.status === 404 &&
    typeof error.data === "object" &&
    error.data !== null &&
    (error.data as { message?: unknown }).message === SESSION_NOT_FOUND_MESSAGE;

interface ReadStoredSessionOptions {
    /**
     * Set by the evaluation poll. Suppresses the FullStory event only: a poll tick is a machine action,
     * not a user journey, and tens of thousands a day would bury the events experts actually generate;
     * the log line stays.
     */
    isPollTick?: boolean;
    /** Set by `readStoredEvaluationWithRetry`. Budgets the request; keeps the FullStory event. */
    isStoredReadAttempt?: boolean;
}

// Every read is budgeted; the flags only decide who owns the retries. A flagged request is one attempt
// of a loop that already has a retry budget, so the shared client's `timeout: 30_000` + ky's 2 default
// GET retries must not nest inside it. An unflagged read runs no loop of its own, so those 2 retries are
// its only retry and are left in place — but it still gets the per-attempt cap, or a single user-facing
// click inherits the client's 30s.
const requestBudget = ({ isPollTick, isStoredReadAttempt }: ReadStoredSessionOptions) => {
    if (isPollTick) return { timeout: EVALUATION_POLL_REQUEST_TIMEOUT_MS, retry: 0 };
    if (isStoredReadAttempt) return { timeout: EVALUATION_STORED_READ_TIMEOUT_MS, retry: 0 };
    return { timeout: EVALUATION_STORED_READ_TIMEOUT_MS };
};

export const getEvaluationResultsBySessionId = async (
    sessionId: string,
    options: ReadStoredSessionOptions = {},
): Promise<StoredSessionReadResult> => {
    const { isPollTick = false } = options;
    const taskId = sessionId ?? sessionIdFromStore;

    try {
        logger.info({ taskId, isPollTick }, "Getting evaluation results by session ID");
        if (!isPollTick) trackFullStoryEvent("Fetching Evaluation Results", { taskId });
        const response = await getSharedApiClient().get(`api/expert-university/v1/session/${sessionId}/evaluation`, {
            ...requestBudget(options),
        });
        // The body is the item itself, with no envelope.
        const item = await response.json<StoredSessionRecord>();
        return { status: "found", item };
    } catch (error: unknown) {
        if (isSessionNotFound(error)) return { status: "absent" };
        // `failed`, never `absent`: a poll must read this as "ask again", not "no results exist".
        const details = getErrorDetails(error);
        logger.info({ ...details, taskId }, "Error getting evaluation results by session ID");
        if (!isPollTick) trackFullStoryEvent("Fetching Evaluation Results Failed", { taskId });
        return { status: "failed", ...details };
    }
};

/**
 * Not sized to the judge: 20s ≈ p75, so most evaluations still resolve inline and the poll covers the
 * tail. Raising it only widens the window in which the client is blind — it cannot show progress or
 * notice a dead socket — and buys nothing, because the server never threads this request's AbortSignal
 * into the judge, so the evaluation persists its verdict whether or not anyone is still listening.
 */
export const EVALUATE_REQUEST_TIMEOUT_MS = 20_000;

// Non-2xx statuses whose body is a status-tagged envelope the caller branches on rather than an opaque
// failure: 422 the pre-judge gates, 409 the server-side attempt cap.
const STRUCTURED_FAILURE_STATUSES: readonly number[] = [409, 422];

// Triggers the expert-university `session/{sessionId}/evaluate` endpoint. This lives on the EWP shared
// gateway (getSharedApiClient, `api/expert-university/v1/...`) alongside the templates/prompt routes — NOT on
// the DS-owned training-session API used by create/update/query. The sessionId is a path param; there is no
// request body. Both `completed` and `already_completed` spread the stored DS item onto the envelope, so
// either can satisfy isEvaluationTranscriptContent. A timeout here is the NORMAL path, not an error — the
// caller is expected to fall back to polling the persisted EvaluationStatus.
export const evaluateTrainingSessionBySessionId = async (sessionId: string): Promise<SessionEvaluationResult> => {
    try {
        logger.info({ sessionId }, "Evaluating training session by session ID");
        trackFullStoryEvent("Training Session Evaluation Triggered", { sessionId });
        const response = await getSharedApiClient().post(`api/expert-university/v1/session/${sessionId}/evaluate`, {
            timeout: EVALUATE_REQUEST_TIMEOUT_MS,
        });
        // 200: either the completed evaluation payload or the idempotent `already_completed` replay envelope.
        return await response.json<SessionEvaluationCompleted | SessionEvaluationSkipped>();
    } catch (error: unknown) {
        // ky pre-parses the body onto `error.data`; the response stream is already consumed, so
        // `error.response.json()` won't work in ky 2.x.
        if (error instanceof HTTPError && STRUCTURED_FAILURE_STATUSES.includes(error.response.status)) {
            const body = error.data;
            if (body && typeof body === "object" && "status" in body) {
                logger.info(
                    { sessionId, evaluationStatus: body.status, statusCode: error.response.status },
                    "Training session not eligible for evaluation",
                );
                return body as SessionEvaluationSkipped | SessionEvaluationRetryExhausted;
            }
        }
        logger.warn({ ...getErrorDetails(error), sessionId }, "Error evaluating training session by session ID");
        trackFullStoryEvent("Training Session Evaluation Failed", { sessionId });
        // 404 -> not_found, any other HTTP status (e.g. 500) -> server_error, no HTTP response -> error.
        const status: SessionEvaluationError["status"] =
            error instanceof HTTPError ? (error.response.status === 404 ? "not_found" : "server_error") : "error";
        return { sessionId, status, ...getErrorDetails(error) };
    }
};
